import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Share2, Linkedin, Facebook, Twitter, Copy, Check, PlayCircle,
  ExternalLink, Clock, AlertCircle, RefreshCw, Loader2, KeyRound,
  LinkIcon, Zap,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  getAdminSocialPosts, testAdminSocialPost,
  getLinkedInStatus, saveLinkedInApp, getLinkedInAuthorizeUrl, disconnectLinkedIn,
  getXStatus, saveXApp, getXAuthorizeUrl, disconnectX,
} from '@/lib/api';
import api from '@/lib/api';

const SITE_URL = 'https://paisleyscotland.com';

// Client-side mirror of backend post-text builder so admin can copy exactly what
// the auto-poster would send. Keep in sync with backend/services/social_poster.py
const buildLinkedInText = (job) => {
  const title = (job?.title || '').trim();
  const employer = (job?.employer || '').trim();
  const location = (job?.location || 'Paisley').trim();
  const slug = (job?.slug || '').trim();
  const url = slug ? `${SITE_URL}/jobs/${slug}` : `${SITE_URL}/jobs`;
  const parts = [`We're hiring: ${title}`];
  if (employer && employer.toLowerCase() !== 'various employers') {
    parts.push(`at ${employer}`);
  }
  parts.push(`in ${location}.`);
  parts.push(`Apply now: ${url}`);
  parts.push('#PaisleyJobs #Renfrewshire #Hiring');
  return parts.join(' ');
};

const buildTweet = (job) => {
  const title = (job?.title || '').trim();
  const location = (job?.location || 'Paisley').trim();
  const slug = (job?.slug || '').trim();
  const url = slug ? `${SITE_URL}/jobs/${slug}` : `${SITE_URL}/jobs`;
  const base = `New job in ${location}: ${title}. Apply: ${url} #PaisleyJobs`;
  if (base.length <= 280) return base;
  const overhead = base.length - title.length;
  const maxTitle = Math.max(20, 280 - overhead - 3);
  const shortTitle = title.slice(0, maxTitle).replace(/\s+$/, '') + '…';
  return `New job in ${location}: ${shortTitle} Apply: ${url} #PaisleyJobs`.slice(0, 280);
};

const StatusBadge = ({ status }) => {
  if (status === 'posted') {
    return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200"><Check className="h-3 w-3 mr-1" /> Posted</Badge>;
  }
  if (status === 'skipped') {
    return <Badge variant="outline" className="text-slate-500"><KeyRound className="h-3 w-3 mr-1" /> Not configured</Badge>;
  }
  if (status === 'failed') {
    return <Badge className="bg-red-100 text-red-700 border-red-200"><AlertCircle className="h-3 w-3 mr-1" /> Failed</Badge>;
  }
  return <Badge variant="outline">{status || 'Unknown'}</Badge>;
};

const ChannelRow = ({ icon: Icon, name, result }) => (
  <div className="flex items-center gap-3 text-sm">
    <Icon className="h-4 w-4 text-slate-400 flex-shrink-0" />
    <span className="font-medium text-slate-700 w-20">{name}</span>
    <StatusBadge status={result?.status} />
    {result?.status === 'failed' && result?.reason && (
      <span className="text-xs text-red-600 truncate max-w-md" title={result.reason}>
        {result.reason}
      </span>
    )}
    {result?.status === 'posted' && result?.post_id && (
      <code className="text-xs text-slate-400 truncate max-w-xs">{result.post_id}</code>
    )}
  </div>
);

const CopyBox = ({ label, text, testId }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(`${label} copied to clipboard`);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error('Copy failed — select and copy manually');
    }
  };
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-slate-600 uppercase tracking-wide">{label}</span>
        <Button
          size="sm"
          variant="outline"
          onClick={handleCopy}
          className="h-7 text-xs"
          data-testid={testId}
        >
          {copied ? <><Check className="h-3 w-3 mr-1" /> Copied</> : <><Copy className="h-3 w-3 mr-1" /> Copy</>}
        </Button>
      </div>
      <Textarea
        value={text}
        readOnly
        rows={3}
        className="text-sm font-mono bg-slate-50"
        data-testid={`${testId}-textarea`}
      />
    </div>
  );
};

export const SocialPostsPanel = () => {
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [items, setItems] = useState([]);
  const [providers, setProviders] = useState({ linkedin: false, facebook: false, x: false });
  const [recentJobs, setRecentJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [linkedinStatus, setLinkedinStatus] = useState(null);
  const [linkedinClientId, setLinkedinClientId] = useState('');
  const [linkedinClientSecret, setLinkedinClientSecret] = useState('');
  const [savingLinkedIn, setSavingLinkedIn] = useState(false);
  const [connectingLinkedIn, setConnectingLinkedIn] = useState(false);
  const [xStatus, setXStatus] = useState(null);
  const [xClientId, setXClientId] = useState('');
  const [xClientSecret, setXClientSecret] = useState('');
  const [savingX, setSavingX] = useState(false);
  const [connectingX, setConnectingX] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [logRes, jobsRes, liRes, xRes] = await Promise.all([
        getAdminSocialPosts(50),
        api.get('/admin/jobs'),
        getLinkedInStatus().catch(() => ({ data: null })),
        getXStatus().catch(() => ({ data: null })),
      ]);
      setItems(logRes.data?.items || []);
      setProviders(logRes.data?.providers || { linkedin: false, facebook: false, x: false });
      setLinkedinStatus(liRes.data || null);
      setXStatus(xRes.data || null);
      const jobs = (jobsRes.data || [])
        .filter(j => j.is_active && j.is_published !== false)
        .slice(0, 10);
      setRecentJobs(jobs);
      if (jobs.length && !selectedJob) setSelectedJob(jobs[0]);
    } catch (e) {
      // 401 is expected when the route-guard hasn't finished redirecting to /admin/login yet.
      // Only toast for genuine failures.
      if (e?.response?.status !== 401) {
        console.error(e);
        toast.error('Failed to load social posts log');
      }
    } finally {
      setLoading(false);
    }
  }, [selectedJob]);

  useEffect(() => { load(); }, [load]);

  // Handle LinkedIn OAuth callback query params
  useEffect(() => {
    const li = searchParams.get('linkedin');
    if (li === 'connected') {
      toast.success('LinkedIn connected successfully!');
      searchParams.delete('linkedin');
      setSearchParams(searchParams, { replace: true });
    } else if (li === 'error') {
      const reason = searchParams.get('reason') || 'unknown';
      toast.error(`LinkedIn connect failed: ${reason}`);
      searchParams.delete('linkedin');
      searchParams.delete('reason');
      setSearchParams(searchParams, { replace: true });
    }
    const x = searchParams.get('x');
    if (x === 'connected') {
      toast.success('X (Twitter) connected successfully!');
      searchParams.delete('x');
      setSearchParams(searchParams, { replace: true });
    } else if (x === 'error') {
      const reason = searchParams.get('reason') || 'unknown';
      toast.error(`X connect failed: ${reason}`);
      searchParams.delete('x');
      searchParams.delete('reason');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleSaveLinkedInApp = async () => {
    if (!linkedinClientId || !linkedinClientSecret) {
      toast.error('Enter both Client ID and Client Secret');
      return;
    }
    setSavingLinkedIn(true);
    try {
      await saveLinkedInApp(linkedinClientId, linkedinClientSecret);
      toast.success('LinkedIn app credentials saved');
      setLinkedinClientSecret(''); // don't keep secret in state after save
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSavingLinkedIn(false);
    }
  };

  const handleConnectLinkedIn = async (scope = 'default') => {
    setConnectingLinkedIn(true);
    try {
      const res = await getLinkedInAuthorizeUrl(scope);
      window.location.href = res.data.authorize_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start LinkedIn OAuth');
      setConnectingLinkedIn(false);
    }
  };

  const handleDisconnectLinkedIn = async () => {
    if (!confirm('Disconnect LinkedIn? Existing auto-posts will stop until you reconnect.')) return;
    try {
      await disconnectLinkedIn();
      toast.success('LinkedIn disconnected');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Disconnect failed');
    }
  };

  const handleSaveXApp = async () => {
    if (!xClientId || !xClientSecret) {
      toast.error('Enter both Client ID and Client Secret');
      return;
    }
    setSavingX(true);
    try {
      await saveXApp(xClientId, xClientSecret);
      toast.success('X app credentials saved');
      setXClientSecret('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSavingX(false);
    }
  };

  const handleConnectX = async () => {
    setConnectingX(true);
    try {
      const res = await getXAuthorizeUrl();
      window.location.href = res.data.authorize_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start X OAuth');
      setConnectingX(false);
    }
  };

  const handleDisconnectX = async () => {
    if (!confirm('Disconnect X? Existing auto-posts will stop until you reconnect.')) return;
    try {
      await disconnectX();
      toast.success('X disconnected');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Disconnect failed');
    }
  };

  const runTest = async () => {
    setTesting(true);
    try {
      const res = await testAdminSocialPost();
      const posted = res.data?.posted?.[0];
      if (!posted) {
        toast.warning('No live job found to post');
      } else {
        const anySuccess = Object.values(posted.channels || {}).some(c => c.status === 'posted');
        if (anySuccess) {
          toast.success('Test post sent — check the log below');
        } else {
          toast.info('Test complete — no channels configured yet, see log for details');
        }
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test post failed');
    } finally {
      setTesting(false);
    }
  };

  const linkedInText = selectedJob ? buildLinkedInText(selectedJob) : '';
  const tweetText = selectedJob ? buildTweet(selectedJob) : '';
  const jobUrl = selectedJob?.slug ? `${SITE_URL}/jobs/${selectedJob.slug}` : `${SITE_URL}/jobs`;

  return (
    <div className="space-y-6" data-testid="social-posts-panel">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Share2 className="h-6 w-6 text-sky-600" />
            Social Auto-Posting
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Every daily ClydeFlow import posts up to 2 fresh jobs to the channels below.
            Copy-paste tools while API access is being approved.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="social-refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button
            onClick={runTest}
            disabled={testing || !recentJobs.length}
            className="bg-sky-600 hover:bg-sky-700 text-white"
            data-testid="social-test-post-btn"
          >
            {testing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Posting…</>
                     : <><PlayCircle className="h-4 w-4 mr-2" /> Test-post latest job</>}
          </Button>
        </div>
      </div>

      {/* LinkedIn Connect card */}
      <Card data-testid="linkedin-connect-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Linkedin className="h-4 w-4 text-blue-700" /> LinkedIn Connect
          </CardTitle>
          <CardDescription>
            OAuth-based connection — no need to paste tokens manually. LinkedIn access tokens expire every 60 days;
            just click Connect again if you ever see the token expiring.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {linkedinStatus?.connected ? (
            // Connected state
            <div className="space-y-3">
              <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3">
                <div>
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-emerald-600" />
                    <span className="font-medium text-emerald-800">Connected</span>
                    {linkedinStatus.authorized_as && (
                      <span className="text-sm text-emerald-700">as {linkedinStatus.authorized_as}</span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-emerald-700">
                    {linkedinStatus.org_urn ? (
                      <>Posting to company page: <strong>{linkedinStatus.org_name || linkedinStatus.org_urn}</strong></>
                    ) : (
                      <>Posting as personal profile (Community Management API not yet approved)</>
                    )}
                    {typeof linkedinStatus.expires_in_days === 'number' && (
                      <> · Token expires in {linkedinStatus.expires_in_days} days</>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => handleConnectLinkedIn('elevated')} data-testid="linkedin-reconnect-btn">
                    <RefreshCw className="h-3 w-3 mr-1" /> Reconnect
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleDisconnectLinkedIn} data-testid="linkedin-disconnect-btn">
                    Disconnect
                  </Button>
                </div>
              </div>
              {linkedinStatus.scopes_granted?.length > 0 && (
                <div className="text-xs text-slate-500">
                  Scopes granted: <code className="bg-slate-100 px-1 rounded">{linkedinStatus.scopes_granted.join(' ')}</code>
                </div>
              )}
              {!linkedinStatus.org_urn && (
                <div className="text-xs bg-amber-50 border border-amber-200 rounded-md p-3 text-amber-800">
                  <strong>Waiting for Community Management API?</strong> Once LinkedIn approves the product in your developer app,
                  click <em>Reconnect</em> above to grant the org-posting scope. Until then, posts go to your personal LinkedIn feed
                  (still visible to everyone but not attributed to the company page).
                </div>
              )}
            </div>
          ) : linkedinStatus?.app_configured ? (
            // App saved but not connected — show Connect button
            <div className="space-y-3">
              <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-700">
                <div className="font-medium mb-1">App credentials saved ✓</div>
                <div className="text-xs text-slate-500">
                  Before clicking Connect, make sure this exact URL is added under
                  <em> Authorized redirect URLs</em> in your LinkedIn app&apos;s Auth tab:
                </div>
                <code className="block mt-2 text-xs bg-white border border-slate-200 rounded px-2 py-1 font-mono break-all" data-testid="linkedin-callback-url">
                  {window.location.origin}/api/social/linkedin/callback
                </code>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  onClick={() => handleConnectLinkedIn('default')}
                  disabled={connectingLinkedIn}
                  className="bg-blue-700 hover:bg-blue-800 text-white"
                  data-testid="linkedin-connect-btn"
                >
                  {connectingLinkedIn
                    ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Redirecting…</>
                    : <><LinkIcon className="h-4 w-4 mr-2" /> Connect LinkedIn (personal + company page)</>}
                </Button>
                <Button
                  onClick={() => handleConnectLinkedIn('elevated')}
                  disabled={connectingLinkedIn}
                  variant="outline"
                  data-testid="linkedin-connect-elevated-btn"
                >
                  <Zap className="h-4 w-4 mr-2" /> Connect with company page (only if Community Mgmt API approved)
                </Button>
              </div>
            </div>
          ) : (
            // Fresh state — need to save app credentials
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Paste the Client ID and Client Secret from your LinkedIn developer app
                (<a href="https://www.linkedin.com/developers/apps" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">Auth tab <ExternalLink className="h-3 w-3" /></a>).
              </p>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Client ID</Label>
                  <Input
                    value={linkedinClientId}
                    onChange={(e) => setLinkedinClientId(e.target.value)}
                    placeholder="e.g. 78abcdefghi"
                    className="mt-1 font-mono text-sm"
                    data-testid="linkedin-client-id-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">Client Secret</Label>
                  <Input
                    type="password"
                    value={linkedinClientSecret}
                    onChange={(e) => setLinkedinClientSecret(e.target.value)}
                    placeholder="WPL_AP1.xxxxxxxxxxxx"
                    className="mt-1 font-mono text-sm"
                    data-testid="linkedin-client-secret-input"
                  />
                </div>
              </div>
              <Button
                onClick={handleSaveLinkedInApp}
                disabled={savingLinkedIn || !linkedinClientId || !linkedinClientSecret}
                className="bg-blue-700 hover:bg-blue-800 text-white"
                data-testid="linkedin-save-app-btn"
              >
                {savingLinkedIn
                  ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</>
                  : <>Save app credentials →</>}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* X Connect card */}
      <Card data-testid="x-connect-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Twitter className="h-4 w-4 text-slate-800" /> X (Twitter) Connect
          </CardTitle>
          <CardDescription>
            OAuth 2.0 with PKCE + auto-refresh — X access tokens expire every 2 hours but we refresh them for you.
            Free tier gives you 1500 posts/month, plenty for daily job announcements.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {xStatus?.connected ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3">
                <div>
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-emerald-600" />
                    <span className="font-medium text-emerald-800">Connected</span>
                    {xStatus.authorized_as && (
                      <span className="text-sm text-emerald-700">as @{xStatus.authorized_as}</span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-emerald-700">
                    {xStatus.has_refresh_token
                      ? 'Refresh token stored — auto-renews indefinitely'
                      : 'No refresh token — token expires every 2 hours, reconnect needed'}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={handleConnectX} data-testid="x-reconnect-btn">
                    <RefreshCw className="h-3 w-3 mr-1" /> Reconnect
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleDisconnectX} data-testid="x-disconnect-btn">
                    Disconnect
                  </Button>
                </div>
              </div>
              {xStatus.scopes_granted?.length > 0 && (
                <div className="text-xs text-slate-500">
                  Scopes: <code className="bg-slate-100 px-1 rounded">{xStatus.scopes_granted.join(' ')}</code>
                </div>
              )}
            </div>
          ) : xStatus?.app_configured ? (
            <div className="space-y-3">
              <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-700">
                <div className="font-medium mb-1">App credentials saved ✓</div>
                <div className="text-xs text-slate-500">
                  Before clicking Connect, make sure this exact URL is added under
                  <em> Callback URL / Redirect URL</em> in your X app&apos;s <em>User authentication settings</em>:
                </div>
                <code className="block mt-2 text-xs bg-white border border-slate-200 rounded px-2 py-1 font-mono break-all" data-testid="x-callback-url">
                  {window.location.origin}/api/social/x/callback
                </code>
                <div className="text-xs text-slate-500 mt-2">
                  Also set <em>App permissions</em> = <strong>Read and write</strong>, and <em>Type of App</em> = <strong>Web App, Automated App or Bot</strong>.
                </div>
              </div>
              <Button
                onClick={handleConnectX}
                disabled={connectingX}
                className="bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="x-connect-btn"
              >
                {connectingX
                  ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Redirecting…</>
                  : <><LinkIcon className="h-4 w-4 mr-2" /> Connect X (Twitter)</>}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Paste the Client ID and Client Secret from your X developer app
                (<a href="https://developer.x.com/en/portal/dashboard" target="_blank" rel="noopener noreferrer" className="text-slate-800 hover:underline inline-flex items-center gap-1">
                  developer.x.com <ExternalLink className="h-3 w-3" />
                </a>).
                Free tier is enough (1500 posts/month).
              </p>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Client ID (OAuth 2.0 Client ID)</Label>
                  <Input
                    value={xClientId}
                    onChange={(e) => setXClientId(e.target.value)}
                    placeholder="e.g. abc123XYZ-..."
                    className="mt-1 font-mono text-sm"
                    data-testid="x-client-id-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">Client Secret</Label>
                  <Input
                    type="password"
                    value={xClientSecret}
                    onChange={(e) => setXClientSecret(e.target.value)}
                    placeholder="long random string"
                    className="mt-1 font-mono text-sm"
                    data-testid="x-client-secret-input"
                  />
                </div>
              </div>
              <Button
                onClick={handleSaveXApp}
                disabled={savingX || !xClientId || !xClientSecret}
                className="bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="x-save-app-btn"
              >
                {savingX
                  ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</>
                  : <>Save app credentials →</>}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Provider status */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Channel readiness</CardTitle>
          <CardDescription>
            Each channel activates automatically once the required env vars are set in <code className="bg-slate-100 px-1 rounded text-xs">backend/.env</code> on production.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {[
            { key: 'linkedin', name: 'LinkedIn Company Page', icon: Linkedin, vars: ['LINKEDIN_ACCESS_TOKEN', 'LINKEDIN_ORG_URN'] },
            { key: 'facebook', name: 'Facebook Page', icon: Facebook, vars: ['FACEBOOK_PAGE_ID', 'FACEBOOK_PAGE_ACCESS_TOKEN'] },
            { key: 'x', name: 'X (Twitter)', icon: Twitter, vars: ['X_BEARER_TOKEN'] },
          ].map(p => (
            <div key={p.key} className="flex items-center justify-between border border-slate-200 rounded-lg p-3" data-testid={`provider-status-${p.key}`}>
              <div className="flex items-center gap-3">
                <p.icon className="h-5 w-5 text-slate-500" />
                <div>
                  <div className="font-medium text-sm text-slate-800">{p.name}</div>
                  <div className="text-xs text-slate-500 font-mono">{p.vars.join(', ')}</div>
                </div>
              </div>
              {providers[p.key] ? (
                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200"><Check className="h-3 w-3 mr-1" /> Ready</Badge>
              ) : (
                <Badge variant="outline" className="text-slate-500"><KeyRound className="h-3 w-3 mr-1" /> Not configured</Badge>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Copy-paste helper */}
      {recentJobs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Copy-paste helper</CardTitle>
            <CardDescription>
              While API access is pending, pick any live job below and copy the pre-formatted post to paste into LinkedIn / Facebook / X manually.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-2 block">
                Select a live job
              </label>
              <select
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
                value={selectedJob?.id || ''}
                onChange={(e) => setSelectedJob(recentJobs.find(j => j.id === e.target.value))}
                data-testid="social-job-select"
              >
                {recentJobs.map(j => (
                  <option key={j.id} value={j.id}>
                    {j.title}{j.employer ? ` — ${j.employer}` : ''}{j.location ? ` (${j.location})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {selectedJob && (
              <>
                <div className="grid md:grid-cols-2 gap-4">
                  <CopyBox
                    label="LinkedIn / Facebook post"
                    text={linkedInText}
                    testId="copy-linkedin"
                  />
                  <CopyBox
                    label="X (Twitter) — max 280 chars"
                    text={tweetText}
                    testId="copy-tweet"
                  />
                </div>
                <div className="flex items-center justify-between bg-slate-50 rounded-md px-3 py-2 text-sm">
                  <span className="text-slate-600">Direct link</span>
                  <a
                    href={jobUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sky-600 hover:text-sky-700 flex items-center gap-1"
                    data-testid="social-job-preview-link"
                  >
                    {jobUrl} <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
                <div className="flex flex-wrap gap-2">
                  <a
                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(jobUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="open-linkedin-share"
                  >
                    <Button size="sm" variant="outline" className="text-blue-700 border-blue-200 hover:bg-blue-50">
                      <Linkedin className="h-4 w-4 mr-2" /> Open LinkedIn share
                    </Button>
                  </a>
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(jobUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="open-facebook-share"
                  >
                    <Button size="sm" variant="outline" className="text-blue-800 border-blue-200 hover:bg-blue-50">
                      <Facebook className="h-4 w-4 mr-2" /> Open Facebook share
                    </Button>
                  </a>
                  <a
                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="open-x-share"
                  >
                    <Button size="sm" variant="outline" className="text-slate-700 border-slate-200 hover:bg-slate-50">
                      <Twitter className="h-4 w-4 mr-2" /> Open X compose
                    </Button>
                  </a>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Log */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="h-4 w-4 text-slate-400" />
            Post log <span className="text-slate-400 font-normal text-sm">({items.length})</span>
          </CardTitle>
          <CardDescription>
            Every attempt from the 12:00 daily cron + any manual test posts.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-10 text-center text-slate-500">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
              Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-slate-500 border-2 border-dashed border-slate-200 rounded-lg" data-testid="social-log-empty">
              <Share2 className="h-8 w-8 mx-auto mb-2 text-slate-300" />
              No social posts yet. Once ClydeFlow imports run at 12:00, they&apos;ll show here.
            </div>
          ) : (
            <div className="space-y-3">
              {items.map((item, i) => (
                <div
                  key={`${item.job_id}-${item.posted_at}-${i}`}
                  className="border border-slate-200 rounded-lg p-4 hover:border-slate-300 transition-colors"
                  data-testid={`social-log-item-${i}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <a
                        href={item.job_slug ? `${SITE_URL}/jobs/${item.job_slug}` : `${SITE_URL}/jobs`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-slate-900 hover:text-sky-600 truncate block"
                      >
                        {item.job_title || item.job_id}
                      </a>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {new Date(item.posted_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}
                        {' · '}
                        <span className="uppercase tracking-wide">{item.trigger || 'unknown'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-1.5 pl-1">
                    <ChannelRow icon={Linkedin} name="LinkedIn" result={item.channels?.linkedin} />
                    <ChannelRow icon={Facebook} name="Facebook" result={item.channels?.facebook} />
                    <ChannelRow icon={Twitter} name="X" result={item.channels?.x} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SocialPostsPanel;
